/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BulkCreatePrivilegeCategoriesRequest } from '../models/bulk-create-privilege-categories-request';
import { BulkEditPrivilegeCategoriesRequest } from '../models/bulk-edit-privilege-categories-request';
import { ClinicalFormCategoryPrivilegeGroupedListResponse } from '../models/clinical-form-category-privilege-grouped-list-response';
import { ClinicalPrivilegeFormPreviewResponse } from '../models/clinical-privilege-form-preview-response';
import { ClinicalPrivilegeHistoryListResponse } from '../models/clinical-privilege-history-list-response';
import { CreatePrivilegeCategoryRequest } from '../models/create-privilege-category-request';
import { PrivilegeCategoryListResponse } from '../models/privilege-category-list-response';
import { PrivilegeCategoryResponse } from '../models/privilege-category-response';
import { UpdatePrivilegeCategoryRequest } from '../models/update-privilege-category-request';

/**
 * Privilege category CRUD under a form category (activate/deactivate + history).
 */
@Injectable({ providedIn: 'root' })
export class ClinicalPrivilegeCategoriesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listClinicalFormCategoryPrivilegesGrouped()` */
  static readonly ListClinicalFormCategoryPrivilegesGroupedPath =
    '/api/v1/clinical-privileges/formcategories/grouped';

  /**
   * Grouped listing for Clinical Form Category Privileges.
   *
   * Returns a 3-tier hierarchical listing: Form Categories -> Privilege Categories -> Privileges,
   * with pagination at the Form Category level, search across form category/privilege category/privilege fields,
   * dynamic sorting, boolean status (active/inactive), and submittedBy (createdBy user).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClinicalFormCategoryPrivilegesGrouped()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalFormCategoryPrivilegesGrouped$Response(
    params?: {
      page?: number;
      size?: number;

      /**
       * Sort order for form categories. Format: column,asc or column,desc. Default: sortOrder,asc.
       */
      sort?: string;

      /**
       * Search form category, privilege category, or privilege fields.
       */
      search?: string;

      /**
       * true returns active items only (status: true); false returns active and inactive items.
       */
      activeOnly?: boolean;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<ClinicalFormCategoryPrivilegeGroupedListResponse>
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.ListClinicalFormCategoryPrivilegesGroupedPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('activeOnly', params.activeOnly, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalFormCategoryPrivilegeGroupedListResponse>;
        })
      );
  }

  /**
   * Grouped listing for Clinical Form Category Privileges.
   *
   * Returns a 3-tier hierarchical listing: Form Categories -> Privilege Categories -> Privileges,
   * with pagination at the Form Category level, search across form category/privilege category/privilege fields,
   * dynamic sorting, boolean status (active/inactive), and submittedBy (createdBy user).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listClinicalFormCategoryPrivilegesGrouped$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalFormCategoryPrivilegesGrouped(
    params?: {
      page?: number;
      size?: number;

      /**
       * Sort order for form categories. Format: column,asc or column,desc. Default: sortOrder,asc.
       */
      sort?: string;

      /**
       * Search form category, privilege category, or privilege fields.
       */
      search?: string;

      /**
       * true returns active items only (status: true); false returns active and inactive items.
       */
      activeOnly?: boolean;
    },
    context?: HttpContext
  ): Observable<ClinicalFormCategoryPrivilegeGroupedListResponse> {
    return this.listClinicalFormCategoryPrivilegesGrouped$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalFormCategoryPrivilegeGroupedListResponse>
        ): ClinicalFormCategoryPrivilegeGroupedListResponse => r.body
      )
    );
  }

  /** Path part for operation `listPrivilegeCategories()` */
  static readonly ListPrivilegeCategoriesPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories';

  /**
   * List privilege categories.
   *
   * **Sortable columns** — use `sort=column,asc` or `sort=column,desc`:
   * - `sortOrder` (default)
   * - `name`, `active`, `createdAt`, `updatedAt`, `id`
   *
   * **Search** — partial match, not case-sensitive:
   * - `name`
   *
   * **Filters:**
   * - `activeOnly` — `true` (default) returns active only; `false` returns active and inactive
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listPrivilegeCategories()` instead.
   *
   * This method doesn't expect any request body.
   */
  listPrivilegeCategories$Response(
    params: {
      id: number;
      activeOnly?: boolean;

      /**
       * Search privilege heading name.
       */
      search?: string;

      /**
       * Sort order. Format: column,asc or column,desc. Default: sortOrder,asc.
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PrivilegeCategoryListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.ListPrivilegeCategoriesPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.query('activeOnly', params.activeOnly, {});
      rb.query('search', params.search, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PrivilegeCategoryListResponse>;
        })
      );
  }

  /**
   * List privilege categories.
   *
   * **Sortable columns** — use `sort=column,asc` or `sort=column,desc`:
   * - `sortOrder` (default)
   * - `name`, `active`, `createdAt`, `updatedAt`, `id`
   *
   * **Search** — partial match, not case-sensitive:
   * - `name`
   *
   * **Filters:**
   * - `activeOnly` — `true` (default) returns active only; `false` returns active and inactive
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listPrivilegeCategories$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listPrivilegeCategories(
    params: {
      id: number;
      activeOnly?: boolean;

      /**
       * Search privilege heading name.
       */
      search?: string;

      /**
       * Sort order. Format: column,asc or column,desc. Default: sortOrder,asc.
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<PrivilegeCategoryListResponse> {
    return this.listPrivilegeCategories$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<PrivilegeCategoryListResponse>
        ): PrivilegeCategoryListResponse => r.body
      )
    );
  }

  /** Path part for operation `createPrivilegeCategory()` */
  static readonly CreatePrivilegeCategoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories';

  /**
   * Create privilege category.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createPrivilegeCategory()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPrivilegeCategory$Response(
    params: {
      id: number;
      body: CreatePrivilegeCategoryRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PrivilegeCategoryResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.CreatePrivilegeCategoryPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PrivilegeCategoryResponse>;
        })
      );
  }

  /**
   * Create privilege category.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createPrivilegeCategory$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPrivilegeCategory(
    params: {
      id: number;
      body: CreatePrivilegeCategoryRequest;
    },
    context?: HttpContext
  ): Observable<PrivilegeCategoryResponse> {
    return this.createPrivilegeCategory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<PrivilegeCategoryResponse>
        ): PrivilegeCategoryResponse => r.body
      )
    );
  }

  /** Path part for operation `bulkCreatePrivilegeCategories()` */
  static readonly BulkCreatePrivilegeCategoriesPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/bulk';

  /**
   * Bulk-create privilege categories with privileges.
   *
   * Creates multiple privilege categories, each with their privileges, under an existing form category in a single atomic call. All rows are new inserts — existing categories and privileges are not affected. Publishes a new catalog version on success. sortOrder defaults to array index if omitted. code is optional — auto-generated as a UUID if not provided.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkCreatePrivilegeCategories()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkCreatePrivilegeCategories$Response(
    params: {
      id: number;
      body: BulkCreatePrivilegeCategoriesRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.BulkCreatePrivilegeCategoriesPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>;
        })
      );
  }

  /**
   * Bulk-create privilege categories with privileges.
   *
   * Creates multiple privilege categories, each with their privileges, under an existing form category in a single atomic call. All rows are new inserts — existing categories and privileges are not affected. Publishes a new catalog version on success. sortOrder defaults to array index if omitted. code is optional — auto-generated as a UUID if not provided.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `bulkCreatePrivilegeCategories$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkCreatePrivilegeCategories(
    params: {
      id: number;
      body: BulkCreatePrivilegeCategoriesRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeFormPreviewResponse> {
    return this.bulkCreatePrivilegeCategories$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>
        ): ClinicalPrivilegeFormPreviewResponse => r.body
      )
    );
  }

  /** Path part for operation `getPrivilegeCategoriesPreview()` */
  static readonly GetPrivilegeCategoriesPreviewPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/preview';

  /**
   * Get all privilege categories with their privileges for a form category.
   *
   * Returns the full hierarchical structure of privilege categories and their nested privileges for the specified form category, based on the latest published catalog version. The response shape is identical to bulkCreatePrivilegeCategories so that the frontend can consume both APIs consistently.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPrivilegeCategoriesPreview()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPrivilegeCategoriesPreview$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.GetPrivilegeCategoriesPreviewPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>;
        })
      );
  }

  /**
   * Get all privilege categories with their privileges for a form category.
   *
   * Returns the full hierarchical structure of privilege categories and their nested privileges for the specified form category, based on the latest published catalog version. The response shape is identical to bulkCreatePrivilegeCategories so that the frontend can consume both APIs consistently.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPrivilegeCategoriesPreview$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPrivilegeCategoriesPreview(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeFormPreviewResponse> {
    return this.getPrivilegeCategoriesPreview$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>
        ): ClinicalPrivilegeFormPreviewResponse => r.body
      )
    );
  }

  /** Path part for operation `bulkEditPrivilegeCategories()` */
  static readonly BulkEditPrivilegeCategoriesPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/preview';

  /**
   * Bulk-edit privilege categories with privileges.
   *
   * Replaces the entire privilege category structure for a form category. Performs a complete replacement: deletes all existing categories and privileges for this form category, then recreates them from the request payload. Publishes a new catalog version on success. IDs in the request are ignored — all rows are treated as new inserts. Use for full-preview edits.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkEditPrivilegeCategories()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkEditPrivilegeCategories$Response(
    params: {
      id: number;
      body: BulkEditPrivilegeCategoriesRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.BulkEditPrivilegeCategoriesPath,
      'put'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>;
        })
      );
  }

  /**
   * Bulk-edit privilege categories with privileges.
   *
   * Replaces the entire privilege category structure for a form category. Performs a complete replacement: deletes all existing categories and privileges for this form category, then recreates them from the request payload. Publishes a new catalog version on success. IDs in the request are ignored — all rows are treated as new inserts. Use for full-preview edits.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `bulkEditPrivilegeCategories$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkEditPrivilegeCategories(
    params: {
      id: number;
      body: BulkEditPrivilegeCategoriesRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeFormPreviewResponse> {
    return this.bulkEditPrivilegeCategories$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeFormPreviewResponse>
        ): ClinicalPrivilegeFormPreviewResponse => r.body
      )
    );
  }

  /** Path part for operation `getPrivilegeCategory()` */
  static readonly GetPrivilegeCategoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}';

  /**
   * Get privilege category.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPrivilegeCategory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPrivilegeCategory$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PrivilegeCategoryResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.GetPrivilegeCategoryPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PrivilegeCategoryResponse>;
        })
      );
  }

  /**
   * Get privilege category.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPrivilegeCategory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPrivilegeCategory(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<PrivilegeCategoryResponse> {
    return this.getPrivilegeCategory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<PrivilegeCategoryResponse>
        ): PrivilegeCategoryResponse => r.body
      )
    );
  }

  /** Path part for operation `updatePrivilegeCategory()` */
  static readonly UpdatePrivilegeCategoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}';

  /**
   * Update privilege category.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePrivilegeCategory()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePrivilegeCategory$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      body: UpdatePrivilegeCategoryRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PrivilegeCategoryResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.UpdatePrivilegeCategoryPath,
      'put'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PrivilegeCategoryResponse>;
        })
      );
  }

  /**
   * Update privilege category.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePrivilegeCategory$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePrivilegeCategory(
    params: {
      id: number;
      privilegeCategoryId: number;
      body: UpdatePrivilegeCategoryRequest;
    },
    context?: HttpContext
  ): Observable<PrivilegeCategoryResponse> {
    return this.updatePrivilegeCategory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<PrivilegeCategoryResponse>
        ): PrivilegeCategoryResponse => r.body
      )
    );
  }

  /** Path part for operation `activatePrivilegeCategory()` */
  static readonly ActivatePrivilegeCategoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/activate';

  /**
   * Activate privilege category.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `activatePrivilegeCategory()` instead.
   *
   * This method doesn't expect any request body.
   */
  activatePrivilegeCategory$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PrivilegeCategoryResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.ActivatePrivilegeCategoryPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PrivilegeCategoryResponse>;
        })
      );
  }

  /**
   * Activate privilege category.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `activatePrivilegeCategory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  activatePrivilegeCategory(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<PrivilegeCategoryResponse> {
    return this.activatePrivilegeCategory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<PrivilegeCategoryResponse>
        ): PrivilegeCategoryResponse => r.body
      )
    );
  }

  /** Path part for operation `deactivatePrivilegeCategory()` */
  static readonly DeactivatePrivilegeCategoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/deactivate';

  /**
   * Deactivate privilege category.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deactivatePrivilegeCategory()` instead.
   *
   * This method doesn't expect any request body.
   */
  deactivatePrivilegeCategory$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PrivilegeCategoryResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.DeactivatePrivilegeCategoryPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PrivilegeCategoryResponse>;
        })
      );
  }

  /**
   * Deactivate privilege category.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deactivatePrivilegeCategory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deactivatePrivilegeCategory(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<PrivilegeCategoryResponse> {
    return this.deactivatePrivilegeCategory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<PrivilegeCategoryResponse>
        ): PrivilegeCategoryResponse => r.body
      )
    );
  }

  /** Path part for operation `getPrivilegeCategoryHistory()` */
  static readonly GetPrivilegeCategoryHistoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/history';

  /**
   * Privilege heading history.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPrivilegeCategoryHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPrivilegeCategoryHistory$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeHistoryListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCategoriesService.GetPrivilegeCategoryHistoryPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeHistoryListResponse>;
        })
      );
  }

  /**
   * Privilege heading history.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPrivilegeCategoryHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPrivilegeCategoryHistory(
    params: {
      id: number;
      privilegeCategoryId: number;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeHistoryListResponse> {
    return this.getPrivilegeCategoryHistory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeHistoryListResponse>
        ): ClinicalPrivilegeHistoryListResponse => r.body
      )
    );
  }
}
